import Vue from 'vue'
import * as types from 'store/mutation-types'
import {
  fetchTradePermission,
  fetchQuotePermission,
  fetchAccountStatusInfo,
  reqDeviceSwitch,
  reqTradeAuthorize,
  fetchUserInfo,
  refreshAuthToken,
  setTwoStepVerify,
  signinAccount,
  signoutAccount,
  fastSigninAccount,
  signupAccount,
  fetchSwitchTradeAccount
} from 'apis/oauth'
import {
  fetchCustomerAccountInfo,
  fetchCustomerAccountProcessInfo,
  fetchVirtualAccounts
} from 'apis/customer'
import {
  ACCOUNT_OPEN_STATUS,
  ACCOUNT_TYPE,
  ASSET_ACCOUNT_TYPE,
  VIRTUAL_TEMPLATE_ID
} from 'const/account'
import { LOCATION_CODE } from 'const/common'
import { OPEN_ACCOUNT_LINK } from 'const/link'
import storage from 'utils/storage'

function getDefaultState() {
  return {
    permission: {
      trade: [],
      quote: [],
      version: 0,
      location: 'CHN',
      hkQuoteType: 'unavailable'
    }, // 权限信息
    status: {
      actualAccountInfo: {
        accountList: [],
        currentCustomerId: '',
        currentAccountId: ''
      },
      virtualAccountInfo: { accountList: [], currentAccountId: '' },
      currentAccountType: '',
      location: LOCATION_CODE.CHN
    }, // 账户状态信息（权限、交易账户）
    identity: {
      access_token: '',
      refresh_token: '',
      uuid: ''
    }, // 用户登录认证信息（token）
    user: {
      uuid: '',
      username: '',
      tel_code: '',
      phone: '',
      email: ''
    }, // 用户基本信息（手机、邮箱）
    info: {
      account_capabilities: '',
      clearing: ''
    }, // 客户开户资料
    process: null, // 开户进度信息（开户-审核-入金-成功）
    tradeToken: '', // 交易授权凭证
    loginKeys: [], // 登录信息记录
    accountVisible: false // 用户弹框显示标记
  }
}

const state = getDefaultState()
// 存储登录账户
function storeLoginAccount(params) {
  storage.set('login-params', {
    grant_type: params.grant_type,
    username: params.username,
    tel_code: params.tel_code
  })
}

const getters = {
  accountVisible: state => state.accountVisible,
  token: state => state.identity.access_token,
  // 判断当前账户是否登录
  isLogin: (state, getters) => !!getters.token,
  // 判断当前账户是否入金
  isDeposited: (state, getters) =>
    getters.isVirtual ||
    getters.curAccount.status === ACCOUNT_OPEN_STATUS.FUNDED,
  isMargin(state) {
    return state.info.account_capabilities === ASSET_ACCOUNT_TYPE.MARGIN
  },
  isCash(state) {
    return state.info.account_capabilities === ASSET_ACCOUNT_TYPE.CASH
  },
  isIb(state, getters) {
    if (getters.isBs || getters.isVirtual) return false
    return state.info.clearing === 'ib'
  },
  isBs(state) {
    return state.status.currentAccountType === ACCOUNT_TYPE.OMNIBUS
  },
  isVirtual(state) {
    return state.status.currentAccountType === ACCOUNT_TYPE.VIRTUAL
  },
  isMarsco(state, getters) {
    if (getters.isBs || getters.isVirtual) return false
    return state.info.clearing === 'marsco'
  },
  // 模拟盘比赛账户
  isVirtualContest(state, getters) {
    return (
      getters.isVirtual &&
      getters.curAccount.template_id !== VIRTUAL_TEMPLATE_ID
    )
  },
  hasActualAccount: state =>
    !!state.status.actualAccountInfo.accountList.length,
  hasVirtualAccount: state =>
    !!state.status.virtualAccountInfo.accountList.length,
  hasAvailableAccount: (state, getters) =>
    getters.hasActualAccount || getters.hasVirtualAccount,
  hasTradePassword: state => !!state.user.trade_password,
  permission: state => state.permission.quote.concat(state.permission.trade),
  loginKeys: state => state.loginKeys,
  email: state => state.user.email || '',
  username: state => state.user.username || '',
  phone: state => state.user.phone || '',
  uuid: state => state.user.uuid || state.identity.uuid || '',
  refreshToken: state => state.identity.refresh_token || '',
  accountProcess: state => {
    if (state.process) {
      return {
        code: state.process.status.step_code,
        name: state.process.status.status,
        btn: state.process.status.btn_text,
        link: state.process.status.link
      }
    }
    return {
      code: 0,
      name: ACCOUNT_OPEN_STATUS.UNACCOUNT,
      link: Vue.filter('langParam')(OPEN_ACCOUNT_LINK),
      btn: Vue.t('trade.acct-status.open')
    }
  },
  // 当前用户的状态信息
  curAccount(state, getters) {
    if (getters.isVirtual) {
      const virtual = state.status.virtualAccountInfo
      return (
        virtual.accountList.find(
          d => d.accountId === virtual.currentAccountId
        ) || {}
      )
    }
    const actual = state.status.actualAccountInfo
    return (
      actual.accountList.find(
        d => d.accountId && d.accountId === actual.currentAccountId
      ) || {}
    )
  },
  hasHkLv2Permission: (state, getters) =>
    getters.permission.includes('hkStockQuoteLv2'),
  hasUkLv1Permission: (state, getters) =>
    getters.permission.includes('ukStockQuoteLv1'),
  hasHkLv0Permission: (state, getters) =>
    getters.permission.includes('hkStockQuoteLv0'),
  hasUsLv0Permission: (state, getters) =>
    getters.permission.includes('usStockQuoteLv0'),
  hasUsLv1Permission: (state, getters) => {
    const permissionList = getters.permission
    // lv1降级和lv1
    return (
      permissionList.includes('usQuoteBasic') ||
      permissionList.includes('usStockQuote')
    )
  },
  hasUsOptionPermission: (state, getters) =>
    getters.permission.includes('usOptionQuote'),
  hasUsOptTradePermission: (state, getters) =>
    [
      'usOptionTrade',
      'usOptionTradeLv1',
      'usOptionTradeLv2',
      'usOptionTradeLv3',
      'usOptionTradeLv4'
    ].some(p => getters.permission.includes(p)),
  hasUsLv2Permission: (state, getters) =>
    getters.permission.includes('usStockQuoteLv2Arca'),
  hasUsQuoteBasic: (state, getters) =>
    getters.permission.includes('usQuoteBasic'),
  hasHkTradePermission: (state, getters) =>
    getters.permission.includes('hkStockTrade'),
  isLocateInChn: (state, getters) => getters.location === LOCATION_CODE.CN,
  tradeToken: state => (state.tradeToken ? state.tradeToken.trade_token : null),
  location: state => state.status.location,
  accountType: state => state.status.currentAccountType,
  hasValidTradeToken(state) {
    return (
      state.tradeToken &&
      state.tradeToken.expires_in >
        (new Date().getTime() - state.tradeToken.create_time) / (1000 * 60)
    )
  },
  // 当前模拟账户（模拟账户有多个）
  curVirtual: (state, getters) => (getters.isVirtual ? getters.curAccount : {}),
  // 当前客户ID（只有实盘账户用这个）
  curCustomerId: (state, getters) => getters.curAccount.customerId,
  // 当前账户ID（模拟盘和实盘都用这个）
  curAccountId: (state, getters) => getters.curAccount.accountId
}

const mutations = {
  [types.UPDATE_ACCOUNT_VISIBLE](state, flag) {
    state.accountVisible = flag
  },
  [types.RESTORE_CACHE_USER](state, data) {
    for (const k in data) {
      Vue.set(state, k, data[k])
    }
  },
  [types.UPDATE_ACCOUNT_STATUS](state, status) {
    Vue.set(state, 'status', { ...state.status, ...status })
  },
  [types.UPDATE_USER_IDENTITY](state, identity) {
    Vue.set(state, 'identity', identity)
  },
  [types.UPDATE_USER_INFO](state, info) {
    Vue.set(state, 'user', { ...state.user, ...info })
  },
  [types.UPDATE_ACCOUNT_INFO](state, info) {
    Vue.set(state, 'info', info)
  },
  [types.UPDATE_ACCOUNT_PROCESS_INFO](state, info) {
    Vue.set(state, 'process', info)
  },
  [types.UPDATE_PERMISSION](state, info) {
    Vue.set(state, 'permission', { ...state.permission, ...info })
  },
  [types.UPDATE_USER_TRADE_TOKEN](state, tokenObj) {
    Vue.set(state, 'tradeToken', tokenObj)
  },
  // 清除所有信息
  [types.CLEAR_USER_INFO](state) {
    const defaultState = getDefaultState()
    for (const key in defaultState) {
      Vue.set(state, key, defaultState[key])
    }
  },
  [types.REFRESH_USER_TOKEN](state, data) {
    const identity = state.identity
    Vue.set(state, 'identity', {
      ...identity,
      access_token: data.access_token,
      refresh_token: data.refresh_token
    })
  },
  [types.UPDATE_USER_TOKEN](state, token) {
    Vue.set(state, 'identity', {
      ...state.identity,
      access_token: token
    })
  },
  [types.SET_LOGIN_TIME](state, [obj, index]) {
    if (index > -1) {
      state.loginKeys[index] = obj
    } else {
      state.loginKeys.push(obj)
    }
  }
}

const actions = {
  loginSuccess({ commit }, data) {
    Vue.track('login_login_successful')
    commit(types.UPDATE_USER_IDENTITY, data)
  },
  async userSignup({ dispatch }, payload) {
    const data = await signupAccount(payload)
    Vue.track('login_signup_successful')
    dispatch('loginSuccess', data)
  },
  async userLogin({ commit, dispatch }, payload) {
    commit(types.CLEAR_USER_INFO)
    const data = await signinAccount(payload)
    storeLoginAccount(payload)
    dispatch('loginSuccess', data)
  },
  async userFastLogin({ dispatch }, payload) {
    const data = await fastSigninAccount(payload)
    storeLoginAccount(payload)
    dispatch('loginSuccess', data)
  },
  async getUserInfo({ commit }) {
    const data = await fetchUserInfo()
    commit(types.UPDATE_USER_INFO, data)
  },
  async getAccountStatus({ commit }) {
    const { tradePermission, ...status } = await fetchAccountStatusInfo()
    commit(types.UPDATE_PERMISSION, { trade: tradePermission })
    // 模拟盘需要获取模拟盘详情并附在status上
    if (status.virtualAccountInfo.accountList.length) {
      const virtual = await fetchVirtualAccounts()
      const accounts = virtual.accounts || []
      status.virtualAccountInfo.accountList = accounts.map(d => {
        d.accountId = d.account_id_str
        return d
      })
    }
    // 实盘需要过滤无效数据
    if (status.actualAccountInfo) {
      const accountList = (status.actualAccountInfo.accountList || []).filter(
        d => d.customerId
      )
      status.actualAccountInfo.accountList = accountList
    }
    if (status.currentAccountType === ACCOUNT_TYPE.VIRTUAL) {
      commit(types.UPDATE_ACCOUNT_STATUS, status)
    } else {
      const actual = status.actualAccountInfo
      const curAccount = actual.accountList.find(
        d => d.accountId && d.accountId === actual.currentAccountId
      )
      if (curAccount) {
        const info = await fetchCustomerAccountInfo({
          customer_id: curAccount.customerId
        })
        commit(types.UPDATE_ACCOUNT_INFO, info)
      }
      commit(types.UPDATE_ACCOUNT_STATUS, status)
    }
  },
  async getAccountProcess({ commit, getters }) {
    if (getters.isVirtual) return
    const process = await fetchCustomerAccountProcessInfo({
      customer_id: getters.curAccount.customerId
    })
    commit(types.UPDATE_ACCOUNT_PROCESS_INFO, process)
  },
  async getVirtualAccounts({ commit, state }) {
    const virtual = await fetchVirtualAccounts()
    const accountList = (virtual.accounts || []).map(d => {
      d.accountId = d.account_id_str
      return d
    })

    commit(types.UPDATE_ACCOUNT_STATUS, {
      virtualAccountInfo: { ...state.status.virtualAccountInfo, accountList }
    })
  },
  async getTradePermission({ commit, getters }) {
    const params = { account_type: getters.accountType }
    if (getters.curAccount.accountId) {
      params.account_id = getters.curAccount.accountId
    }
    if (getters.curAccount.customerId) {
      params.customerId = getters.curAccount.customerId
    }
    const data = await fetchTradePermission(params)
    commit(types.UPDATE_PERMISSION, {
      trade: data.tradePermission || [],
      location: data.location
    })
  },
  async getQuotePermission({ commit, state }) {
    const {
      permVersion,
      permission,
      hk_quote_type
    } = await fetchQuotePermission()

    if (permVersion < state.permission.version) return

    commit(types.UPDATE_PERMISSION, {
      quote: permission,
      version: permVersion,
      hk_quote_type
    })
  },
  async switchCurrentDevice({ dispatch, commit }) {
    const data = await reqDeviceSwitch()
    commit(types.UPDATE_USER_IDENTITY, data)
    return dispatch('refreshToken')
  },
  async changeAccount({ dispatch, commit }, payload) {
    const params = { account_type: payload.accountType }
    if (payload.accountId) {
      params.account_id = payload.accountId
    }
    if (payload.customerId) {
      params.customer_id = payload.customerId
    }
    await fetchSwitchTradeAccount(params)
    await dispatch('getAccountStatus')
    commit(types.DELETE_USER_HOLDING_ASSET)
  },
  async tradeLogin({ commit }, params) {
    const { data: respData } = await reqTradeAuthorize(params)
    if (respData.is_succ) {
      commit(
        types.UPDATE_USER_TRADE_TOKEN,
        Object.assign(respData.data, {
          create_time: new Date().getTime()
        })
      )
      return respData.data
    } else {
      return Promise.reject(respData.data)
    }
  },
  async refreshToken({ commit, getters }) {
    const { data: respData } = await refreshAuthToken({
      grant_type: 'refresh_token',
      refresh_token: getters.refreshToken
    })
    if (respData.is_succ) {
      commit(types.REFRESH_USER_TOKEN, respData.data)

      return respData.data
    } else {
      return Promise.reject(respData.data)
    }
  },
  setTwoStepVerify({ dispatch }, params) {
    return setTwoStepVerify(params)
  },
  logout({ commit }) {
    signoutAccount()
    commit(types.CLEAR_USER_INFO)
    commit(types.DELETE_USER_HOLDING_ASSET)
  }
}
export default {
  state,
  getters,
  mutations,
  actions
}
